import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * GAU one-tap add-ons for the cart drawer and cart page.
 * Tapping "Add" on a suggestion adds one unit of that variant and refreshes the cart
 * through the same event the theme's own cart components use, so the drawer or page
 * updates itself (totals, progress bar, gifts). Uses event delegation, so it keeps
 * working after the cart HTML is refreshed.
 *
 * SPEED: the button answers the tap instantly ("Adding..."), and only ONE request is sent
 * (the old version first downloaded the whole cart, then added). A suggestion is never a product
 * that is already in the cart (the list is rebuilt after every cart change), so quantity 1 is correct.
 */
const SOURCE = 'gau-cart-addons';
const TIMEOUT_MS = 12000;
const adding = new Set();

/** @returns {string} Comma-separated section ids of every rendered cart-items-component. */
function collectSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    const id = element.getAttribute('data-section-id');
    if (id) ids.add(id);
  });
  return Array.from(ids).join(',');
}

/** @param {number} variantId */
async function addOne(variantId) {
  const quantity = 1;
  const body = JSON.stringify({
    updates: { [variantId]: quantity },
    sections: collectSectionIds(),
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ id: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', { body }),
      signal: controller.signal,
    });
    const parsed = await response.json();
    if (parsed.errors) {
      throw new Error(typeof parsed.errors === 'string' ? parsed.errors : JSON.stringify(parsed.errors));
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed),
      detail: {
        sections: parsed.sections,
        items: parsed.items,
        itemCount: parsed.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error instanceof Error ? error : new Error(String(error)));
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-gau-add]') : null;
  if (!button) return;
  event.preventDefault();

  const variantId = Number(button.getAttribute('data-gau-add'));
  if (!Number.isFinite(variantId) || variantId <= 0 || adding.has(variantId)) return;

  adding.add(variantId);
  const label = button.textContent;
  button.classList.add('is-busy');
  button.textContent = 'Adding…';

  addOne(variantId)
    .catch((error) => {
      console.warn('[gau-cart-addons] Could not add the product:', error);
      button.classList.remove('is-busy');
      button.textContent = label;
    })
    .finally(() => {
      adding.delete(variantId);
    });
});